/**
 * Published-resource versioning types (task 725).
 *
 * Report: `project/reports/innovate-service-published-resource-versioning.md`.
 *
 * The one rule the whole feature exists to express:
 *
 *   > A published version is immutable. Edits always land on an unpublished
 *   > working version.
 *
 * These shapes mirror the `innovate-service` version store's own vocabulary
 * (`src/lib/versioning/documents.ts`, `src/processes/resourceVersion.ts`) so
 * the app never re-labels what the service already names — `entityType` values
 * are the service's snake_case identifiers, and a version is carried both as
 * its `(major, minor)` integer pair and as the pre-formatted `version` label
 * the service builds, because `1.10` is the tenth minor of major 1 and must
 * never be reconstructed through a float.
 */

/** The four resource types the MVP versions. LlmConfig/McpConnection are not versioned. */
export const VERSIONED_ENTITY_TYPES = [
  'endpoint',
  'collaboration_config',
  'agent',
  'prompt',
] as const;

export type VersionedEntityType = (typeof VERSIONED_ENTITY_TYPES)[number];

/** Human labels for the entity types, used in cascade lists and pin sets. */
export const VERSIONED_ENTITY_LABELS: Record<VersionedEntityType, string> = {
  endpoint: 'AI Service',
  collaboration_config: 'Config',
  agent: 'Agent',
  prompt: 'Prompt',
};

/** A version row is either the single mutable draft or a frozen publication. */
export type ResourceVersionStatus = 'working' | 'published';

/**
 * The four states the read-only status indicator renders (report, "What the
 * user sees"). `live-editing` is the dormant state: the Account has not adopted
 * the practice, so no versioning vocabulary appears anywhere.
 */
export type ResourceVersionState =
  | 'live-editing'
  | 'tracked-unpublished'
  | 'published-clean'
  | 'draft-in-progress';

/** Everything the status indicator needs, in one call. */
export interface ResourceVersionStatusView {
  state: ResourceVersionState;
  versioningEnabled: boolean;
  /** e.g. `"1.0"`. Null until the resource's first publish. */
  publishedVersion: string | null;
  /** e.g. `"1.1"`. Null when the resource is clean. */
  workingVersion: string | null;
}

/**
 * The account-wide, one-way opt-in. There is deliberately no disable shape:
 * un-versioning resources whose published versions are serving has no safe
 * meaning, so the app renders the enabled state as informational only.
 */
export interface AccountVersioningSettings {
  accountId: string;
  versioningEnabled: boolean;
  versioningEnabledAt: string | null;
  versioningEnabledBy: string | null;
}

/**
 * A version row as the history list renders it. The service returns the whole
 * stored row, so `document` rides along on list rows too — history is read
 * whole rather than queried by field, which is exactly the trade-off the
 * generic version store makes.
 */
export interface ResourceVersionSummary {
  id: string;
  entityType: VersionedEntityType;
  entityId: string;
  accountId?: string;
  major: number;
  minor: number;
  /** Pre-formatted `{major}.{minor}` label from the service. */
  version: string;
  status: ResourceVersionStatus;
  publishedAt: string | null;
  publishedBy: string | null;
  notes: string | null;
  createdAt: string;
  document?: Record<string, unknown> | null;
  /**
   * The lockfile this endpoint version froze. Endpoint versions only, and only
   * once the service serves it on a version read — it is served on the publish
   * preview today, which is where the publish dialog reads it from.
   */
  pinSet?: PinSet | null;
}

export type ResourceVersionDetail = ResourceVersionSummary;

/**
 * The pin set an endpoint publication records: the exact versions that will
 * serve, plus the dependencies that deliberately are NOT frozen.
 *
 * `liveReferences` is the honest half. LlmConfig and McpConnection resolve
 * fresh at execution, so a published endpoint version is not frozen on the LLM
 * axis — the report accepts that knowingly, on condition the UI never lets a
 * pin set look like it freezes more than it does.
 */
export interface PinnedVersion {
  entityType: VersionedEntityType;
  entityId: string;
  major: number;
  minor: number;
}

export interface LiveReference {
  entityType: 'llm_config' | 'mcp_connection';
  entityId: string;
}

export interface PinSet {
  pinned: PinnedVersion[];
  liveReferences: LiveReference[];
  /** GeneratedEndpointUi keeps its own version model; pinned by version id. */
  generatedUi: Array<{ generatedUiId: string; versionId: string; version: number }>;
}

/**
 * One dependency the cascade will publish along with the root. The confirmation
 * dialog renders this list verbatim before anything is frozen — that list is
 * what keeps the auto-publish cascade explicit without making it manual.
 */
export interface CascadeEntry {
  entityType: VersionedEntityType;
  entityId: string;
  name: string;
  /** The version this dependency will be frozen as. */
  version: string;
  reason: 'never-published' | 'has-unpublished-changes';
}

/** The dry-run answer to "what happens if I publish this right now?". */
export interface PublishPreview {
  entityType: VersionedEntityType;
  entityId: string;
  /** The version the root resource will take, given the requested `majorBump`. */
  version: string;
  firstPublish: boolean;
  cascade: CascadeEntry[];
  /** What the endpoint's published version will pin. Null for non-endpoints. */
  pinSet: PinSet | null;
}

export interface PublishRequest {
  /** "Publish as v2.0" — resets minor to 0. */
  majorBump?: boolean;
  notes?: string;
  /**
   * Where to deploy the newly frozen version (task 760). A *request*, not an
   * instruction: the service decides by the shape of the Account and answers on
   * `PublishResult.environment`. Omit it and an Account with exactly one
   * environment still deploys there — asking which one is a question with a
   * single answer — while an Account with several publishes only.
   */
  environment?: string;
}

export interface PublishResult {
  entityType: VersionedEntityType;
  entityId: string;
  /** The version that was frozen. */
  version: string;
  firstPublish: boolean;
  /** Everything the transaction published, root and cascade alike. */
  published: Array<{ entityType: string; entityId: string; version: string }>;
  pinSet: PinSet | null;
  /**
   * Where the freshly published version actually landed — **what happened**, not
   * an echo of what was asked, so a caller can never mistake "field accepted"
   * for "deployed". Null when the publish deployed nowhere.
   *
   * The service decides by the shape of the Account (task 759): no environments
   * publishes only; exactly one deploys there without asking; several deploy
   * only when one is named, because choosing among stages is a decision a person
   * makes. A named environment that does not exist publishes anyway and comes
   * back with `environmentIgnored: true` rather than failing the publish.
   */
  environment?: string | null;
  environmentIgnored?: boolean;
}

/**
 * One line of a pin set as the UI renders it — the two kinds flattened into a
 * single list so the panel can lay them out together while still telling them
 * apart. Built from a `PinSet` by `flattenPinSet`.
 */
export interface PinSetEntry {
  kind: 'version' | 'reference';
  entityType: string;
  entityId: string;
  name?: string | null;
  /** Present only on `kind: 'version'`. */
  version?: string | null;
}

/** Flatten a stored pin set into renderable lines, pinned versions first. */
export const flattenPinSet = (pinSet: PinSet | null | undefined): PinSetEntry[] => {
  if (!pinSet) return [];
  return [
    ...(pinSet.pinned ?? []).map<PinSetEntry>((entry) => ({
      kind: 'version',
      entityType: entry.entityType,
      entityId: entry.entityId,
      version: `${entry.major}.${entry.minor}`,
    })),
    ...(pinSet.generatedUi ?? []).map<PinSetEntry>((entry) => ({
      kind: 'version',
      entityType: 'generated_ui',
      entityId: entry.generatedUiId,
      version: String(entry.version),
    })),
    ...(pinSet.liveReferences ?? []).map<PinSetEntry>((entry) => ({
      kind: 'reference',
      entityType: entry.entityType,
      entityId: entry.entityId,
    })),
  ];
};
